import {Behavior} from 'app_api/projects/behaviors/types';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {Translatable} from 'common/i18n';

import {FunnelSectionItem, MenuType, MultiMetricsSection, RetentionSectionItem} from '../multi-metrics-menu/types';

function getBehaviorMenuItem(
  behavior: Behavior,
  metricType: `funnel` | `retention`,
): FunnelSectionItem | RetentionSectionItem {
  const menuType = metricType === `funnel` ? MenuType.Funnel : MenuType.Retention;
  const icon = {
    [MenuType.Funnel]: `brand-funnels`,
    [MenuType.Retention]: `brand-retention`,
  }[menuType];

  return {
    icon,
    label: behavior.name,
    menuType,
    value: behavior,
  };
}

export function getSavedBehaviorSection({
  label,
  metricType,
  savedBehaviors,
  isRecentSection = false,
}: {
  label: Translatable;
  metricType: `funnel` | `retention`;
  savedBehaviors: Behavior[];
  isRecentSection?: boolean;
}): MultiMetricsSection<Behavior> {
  return {
    label,
    isRecentSection,
    items: savedBehaviors.map((behavior) => getBehaviorMenuItem(behavior, metricType)),
  };
}

export function getSavedBehaviorSections({
  recentBehaviorMenuItems,
  metricType,
  savedBehaviors,
}: {
  recentBehaviorMenuItems: Behavior[];
  metricType: `funnel` | `retention`;
  savedBehaviors: Behavior[];
}): Array<MultiMetricsSection<Behavior>> {
  if (savedBehaviors.length) {
    const recentBehaviors = savedBehaviors.filter((behavior) =>
      recentBehaviorMenuItems.some((item) => item.id === behavior.id),
    );

    const showRecents = recentBehaviors.length > 6 && recentBehaviors.length > 0;
    return [
      /** only disply a recently used section when there are enough items to fill in the dropdown */
      getSavedBehaviorSection({
        label: ({t}) => t(`query-builder.multi-metrics-menu.section-recents`),
        metricType,
        savedBehaviors: showRecents ? recentBehaviors : [],
        isRecentSection: true,
      }),
      getSavedBehaviorSection({
        label: showRecents
          ? metricType === `funnel`
            ? ({t}) => t(`query-builder.multi-metrics-menu.section-funnel`)
            : ({t}) => t(`query-builder.multi-metrics-menu.section-retention`)
          : ``,
        metricType,
        savedBehaviors,
      }),
    ].filter(Boolean);
  }

  return [];
}

export function useSavedBehaviors({metricType}: {metricType: `funnel` | `retention`}): Behavior[] {
  const behaviors = useBehaviors();
  return (
    behaviors
      .getAllValues()
      // fresh deleted behaviors result in `null` in lexicon
      .filter(Boolean)
      .filter((metric) => metric.type === metricType && metric.can_view)
  );
}
